/**
 * The one place this surface talks to `/guest/v1`.
 *
 * Everything goes through `request` so that four things happen exactly once rather than at every
 * call site:
 *
 *   - **`credentials: 'include'`.** The session is a host-scoped `SameSite=Lax` cookie and the
 *     browser will not send it otherwise. In development the Vite proxy is what makes the origin
 *     match; see `vite.config.ts`.
 *   - **Problem details are parsed.** Section 13 uses RFC 9457, so a failure carries a `detail`
 *     written for the person reading it. Rendering our own sentence instead would throw away copy
 *     that `guest-failures.ts` was careful about - it is explicit that nothing on this surface says
 *     "invalid", "denied" or "forbidden".
 *   - **The correlation id is kept.** Section 13 puts one in every response because it is the only
 *     thing a customer can read back to us over the phone. A prospect on a phone cannot open a
 *     console, so it reaches the screen instead of a log.
 *   - **Responses are validated.** The zod schemas from `shared/contracts` are the contract, and
 *     parsing here means a shape change surfaces as one error rather than as `undefined` rendered
 *     somewhere three components away.
 *
 * ## What is deliberately absent
 *
 * No token handling of any kind. The grant travels in the path and the session travels in a cookie
 * the script cannot read, which is the point of `HttpOnly`. Nothing here stores a credential, and
 * there is no `Authorization` header to set.
 */
import { problemDetailsSchema } from '@iqz-ddds/contracts';
import type { ZodType } from 'zod';

/** Where `/guest/v1` lives. Same origin in production; the dev proxy makes it same origin too. */
const BASE = '/guest/v1';

/**
 * A failure worth showing somebody.
 *
 * `detail` is the server's sentence, never ours. `reference` is the correlation id. `status` is kept
 * because two screens branch on it: a 404 on a grant means the link is spent, and a 401 during
 * verification means the code was wrong rather than the session having lapsed.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly reference: string | undefined,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

/** Thrown when the network never reached us at all. Distinct, because the remedy is different. */
export class OfflineError extends Error {
  constructor() {
    super('You appear to be offline. Check your connection and try again.');
    this.name = 'OfflineError';
  }
}

const CORRELATION_HEADER = 'x-correlation-id';

/**
 * Pull the correlation id out of a response.
 *
 * The header first, then the problem body. The header is present on successes too, and the body
 * carries it on failures the filter rendered, so between them every response has one.
 */
function referenceOf(response: Response, body: unknown): string | undefined {
  const header = response.headers.get(CORRELATION_HEADER);
  if (header !== null && header !== '') return header;

  const parsed = problemDetailsSchema.safeParse(body);
  return parsed.success ? parsed.data.correlationId : undefined;
}

async function bodyOf(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A proxy or an ingress answering with HTML. Not something to show a prospect verbatim.
    return null;
  }
}

export interface RequestOptions<T> {
  readonly method?: 'GET' | 'POST';
  readonly path: string;
  readonly body?: unknown;
  /** Omit for a 204. Present, and the response is parsed through it. */
  readonly schema?: ZodType<T>;
}

export async function request<T>(options: RequestOptions<T>): Promise<T> {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(`${BASE}${options.path}`, {
      method,
      // The whole reason this wrapper exists. Without it the session cookie is not sent and every
      // call after verification answers 401.
      credentials: 'include',
      headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    // `fetch` rejects only when the request never completed: no network, DNS, a dropped connection.
    // A 500 is a resolved promise, so this really is the offline case.
    throw new OfflineError();
  }

  const body = await bodyOf(response);

  if (!response.ok) {
    const problem = problemDetailsSchema.safeParse(body);
    throw new ApiError(
      response.status,
      // The server's sentence where there is one. These are written for a prospect who has clicked
      // a link out of their inbox and may not be sure any of this is real. `detail` is optional in
      // the schema - a problem may carry only a `title` - so there is a fallback for that too.
      (problem.success ? problem.data.detail : undefined) ??
        'Something went wrong. Try again in a moment.',
      referenceOf(response, body),
    );
  }

  if (options.schema === undefined) return undefined as T;

  const parsed = options.schema.safeParse(body);
  if (!parsed.success) {
    // A contract mismatch is our bug, so it says nothing about zod to the person reading it.
    throw new ApiError(
      response.status,
      'The server sent something this page did not understand.',
      referenceOf(response, body),
    );
  }
  return parsed.data;
}
